import {Inject, Injectable} from '@angular/core';
import {ENVIRONMENT, EnvironmentBroker, EnvironmentClient, FeatureFlag} from '@emma-labs/shared/typescript';
import * as LDClient from 'launchdarkly-js-client-sdk';
import {from, Observable, of, switchMap} from 'rxjs';
import {map} from 'rxjs/operators';
import {FeatureFlagService} from '../feature-flag';
import {LocalStorageService} from '../local-storage.service';
import {deepEqual} from '../utility-functions';

@Injectable({
  providedIn: 'root',
})
export class LaunchDarklyService implements FeatureFlagService {
  private readonly ANON_KEY = 'anon-key';
  private ldClient: LDClient.LDClient | undefined;

  constructor(
    @Inject(ENVIRONMENT) private readonly environment: EnvironmentClient | EnvironmentBroker,
    private readonly localStorage: LocalStorageService,
  ) {}

  initialize(key?: string): Observable<void> {
    this.ldClient = LDClient.initialize(
      this.environment.launchDarklyClientId!,
      {
        kind: 'user',
        key: key ?? this.getAnonymousKey(),
        app: this.environment.app,
        anonymous: key === undefined,
      },
      {
        streaming: true,
      },
    );

    return from(this.ldClient.waitForInitialization());
  }

  reinitialize(key?: string): Observable<void> {
    if (!this.ldClient) {
      return this.initialize(key);
    }

    // The client is already initialized, so we just need to update the context via the identify function
    return from(
      this.ldClient!.identify({
        ...this.ldClient!.getContext(),
        key: key ?? this.getAnonymousKey(),
        anonymous: key === undefined,
      }),
    ).pipe(map(() => undefined));
  }

  isEnabled(
    featureFlag: FeatureFlag,
    params?: {
      [p: string]: string | number | boolean;
    },
  ): Observable<boolean> {
    const currContext = this.ldClient!.getContext();
    const newContext: LDClient.LDContext = {
      ...currContext,
      ...params,
    };

    const variationObservable$ = of(this.ldClient!.variation(featureFlag.name, false));
    return deepEqual(currContext, newContext)
      ? variationObservable$
      : from(this.ldClient!.identify(newContext)).pipe(switchMap(() => variationObservable$));
  }

  private getAnonymousKey(): string {
    if (!this.localStorage.getItem(this.ANON_KEY)) {
      const anonKey = crypto.randomUUID();
      this.localStorage.setItem(this.ANON_KEY, anonKey);
    }

    return this.localStorage.getItem(this.ANON_KEY) as string;
  }
}
